<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-title>
              <AppPageHeader
                icon="mdi-receipt-text-outline"
                title="Purchase Bills"
                subtitle="Vendor bills raised against purchase orders"
                style="width: 100%"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      fab
                      x-small
                      color="primary"
                      @click="showCreateDialog = true"
                    >
                      <v-icon dark> mdi-plus </v-icon>
                    </v-btn>
                  </template>
                  <span>Create Purchase Bill</span>
                </v-tooltip>
              </AppPageHeader>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="4">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search Bill #, PO # or Vendor... "
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.vendor_id"
                    :items="vendors"
                    item-text="name"
                    item-value="id"
                    label="Vendor"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-domain"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.payment_status"
                    :items="statusOptions"
                    label="Payment Status"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-cash-check"
                    clearable
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
              <v-simple-table
                v-else
                fixed-header
                height="500"
                class="striped-table"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Sr No</th>
                      <th class="text-left">Bill Number</th>
                      <th class="text-left">PO Number</th>
                      <th class="text-left">Vendor</th>
                      <th class="text-left">Bill Date</th>
                      <th class="text-left">Due Date</th>
                      <th class="text-right">Total</th>
                      <th class="text-right">Paid</th>
                      <th class="text-right">Pending</th>
                      <th class="text-left">Status</th>
                      <th class="text-right action_th">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="bills.length != 0">
                    <tr v-for="(bill, i) in bills" :key="`bill_${i}`">
                      <td style="min-width: 85px">{{ i + 1 }}</td>
                      <td>{{ bill.bill_number }}</td>
                      <td>
                        {{
                          bill.purchase_order
                            ? bill.purchase_order.po_number
                            : ""
                        }}
                      </td>
                      <td>{{ bill.vendor ? bill.vendor.name : "" }}</td>
                      <td>{{ bill.bill_date | niceDate }}</td>
                      <td>{{ $options.filters.niceDate(bill.due_date) || "" }}</td>
                      <td class="text-right mono-figure">{{ bill.total_amount }}</td>
                      <td class="text-right mono-figure">{{ bill.paid_amount }}</td>
                      <td class="text-right mono-figure">{{ bill.pending_amount }}</td>
                      <td>
                        <v-chip
                          small
                          :color="statusColor(bill.payment_status)"
                          text-color="white"
                        >
                          {{ statusLabels[bill.payment_status] }}
                        </v-chip>
                      </td>
                      <td class="text-right">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              class="my-auto"
                              color="primary"
                              :loading="downloadingId === bill.id"
                              @click="downloadInvoice(bill)"
                            >
                              <v-icon dark> mdi-file-download-outline </v-icon>
                            </v-btn>
                          </template>
                          <span>Download Invoice</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              class="ml-2"
                              fab
                              x-small
                              color="primary"
                              @click="openView(bill)"
                            >
                              <v-icon dark> mdi-eye </v-icon>
                            </v-btn>
                          </template>
                          <span>View / Record Payment</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="11">
                        <AppEmptyState
                          icon="mdi-receipt-text-outline"
                          title="No purchase bills yet"
                          message="Raise a bill against a purchase order to start tracking what's owed to a vendor."
                          action-text="Create Purchase Bill"
                          @action="showCreateDialog = true"
                        />
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination v-model="page" :length="paginationLength">
              </v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <AppDialog
        :show.sync="showCreateDialog"
        max-width="1000"
        icon="mdi-receipt-text-outline"
        title="Create Purchase Bill"
        subtitle="Raise a bill against a purchase order"
      >
        <PurchaseBillCreatePage
          v-if="showCreateDialog"
          embedded
          @saved="onDialogSaved"
          @close="showCreateDialog = false"
        />
      </AppDialog>

      <AppDialog
        :show.sync="showViewDialog"
        max-width="900"
        icon="mdi-receipt-text-outline"
        :title="viewingBill ? viewingBill.bill_number : ''"
        :subtitle="viewingBill && viewingBill.vendor ? viewingBill.vendor.name : ''"
        :status-text="viewingBill ? statusLabels[viewingBill.payment_status] : ''"
        :status-color="viewingBill ? statusColor(viewingBill.payment_status) : 'grey'"
      >
        <PurchaseBillEditPage
          v-if="showViewDialog"
          embedded
          :bill-id="viewingId"
          @saved="onDialogSaved"
          @close="showViewDialog = false"
        />
      </AppDialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
import AppDialog from "@/components/AppDialog.vue";
import AppEmptyState from "@/components/AppEmptyState.vue";
import AppPageHeader from "@/components/AppPageHeader.vue";
import PurchaseBillCreatePage from "./create.vue";
import PurchaseBillEditPage from "./id.vue";
export default {
  name: "purchaseBillsPage",
  components: { AppDialog, AppEmptyState, AppPageHeader, PurchaseBillCreatePage, PurchaseBillEditPage },
  data() {
    return {
      filter: {
        vendor_id: "",
        payment_status: "",
      },
      page: 1,
      paginationLength: 0,
      count: 0,
      bills: [],
      vendors: [],
      statusLabels: {
        0: "Unpaid",
        1: "Partial",
        2: "Paid",
      },
      statusOptions: [
        { text: "Unpaid", value: 0 },
        { text: "Partial", value: 1 },
        { text: "Paid", value: 2 },
      ],
      isLoading: false,
      downloadingId: null,
      showCreateDialog: false,
      showViewDialog: false,
      viewingId: null,
      viewingBill: null,
    };
  },
  watch: {
    page: "getData",
  },
  mounted() {
    this.getData();
    this.getMasters();
  },
  methods: {
    async getMasters() {
      await axios
        .get(`masters?context=purchase_bill`)
        .then((response) => {
          this.vendors = response.data.vendors || [];
        })
        .catch(function (error) {
          console.log(error);
        });
    },
    statusColor(status) {
      const colors = { 0: "error", 1: "warning", 2: "success" };
      return colors[status] || "grey";
    },
    openView(bill) {
      this.viewingId = this.encryptIt(bill.id);
      this.viewingBill = bill;
      this.showViewDialog = true;
    },
    onDialogSaved() {
      this.showCreateDialog = false;
      this.getData();
      if (this.$toast) {
        this.$toast.success("Purchase Bill saved successfully");
      }
    },
    async getData() {
      this.isLoading = true;
      this.filter.vendor_id = this.filter.vendor_id
        ? this.filter.vendor_id
        : "";
      this.filter.payment_status =
        this.filter.payment_status === 0 || this.filter.payment_status
          ? this.filter.payment_status
          : "";
      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&vendor_id=" +
        this.filter.vendor_id +
        "&payment_status=" +
        this.filter.payment_status +
        "&search_keyword=" +
        (this.filter.search_keyword || "");
      await axios
        .get(`purchase_bills?${query}`)
        .then((response) => {
          this.bills = response.data.data;
          this.count = response.data.count;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
        })
        .catch(function (error) {
          console.log(error);
        });
      this.isLoading = false;
    },
    async downloadInvoice(bill) {
      this.downloadingId = bill.id;
      try {
        const response = await axios.get(
          `/purchase_bills/${this.encryptIt(bill.id)}/invoice/download`,
          { responseType: "blob" },
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `purchase_bill_${bill.bill_number}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
      }
      this.downloadingId = null;
    },
  },
};
</script>
